<template>
    <div>
        <h4>Shipment Required Fields</h4>
        <md-table class="custom-paginated-table">
            <md-table-row>
                <md-table-head>Field</md-table-head>
                <md-table-head>Required</md-table-head>
            </md-table-row>
            <md-table-row v-for="field in shipmentFields" :key="field.key" v-if="field.visible">
                <md-table-cell>{{ field.label }}</md-table-cell>
                <md-table-cell>
                    <input type="checkbox" v-model="field.checked" @change="markDirty" />
                </md-table-cell>
            </md-table-row>
        </md-table>

        <div class="table-gap"></div>

        <h4>Shipment Pickup Required Fields</h4>
        <md-table class="custom-paginated-table">
            <md-table-row>
                <md-table-head>Field</md-table-head>
                <md-table-head>Required</md-table-head>
            </md-table-row>
            <md-table-row v-for="field in shipmentPickupFields" :key="field.key" v-if="field.visible">
                <md-table-cell>{{ field.label }}</md-table-cell>
                <md-table-cell>
                    <input type="checkbox" v-model="field.checked" @change="markDirty" />
                </md-table-cell>
            </md-table-row>
        </md-table>

        <div class="table-gap"></div>

        <h4>Shipment Dropoff Required Fields</h4>
        <md-table class="custom-paginated-table">
            <md-table-row>
                <md-table-head>Field</md-table-head>
                <md-table-head>Required</md-table-head>
            </md-table-row>
            <md-table-row v-for="field in shipmentDropoffFields" :key="field.key" v-if="field.visible">
                <md-table-cell>{{ field.label }}</md-table-cell>
                <md-table-cell>
                    <input type="checkbox" v-model="field.checked" @change="markDirty" />
                </md-table-cell>
            </md-table-row>
        </md-table>

        <md-button class="md-primary" :disabled="!isDirty" @click="saveChanges">Save Changes</md-button>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { ReverseBitwise, GetBitwiseFlagsValue } from '@/utils/ApplyRatesToUtils';
import {
    CREATE_SHIPMENT_REQUIRED_BOOKING_FIELDS,
    CREATE_SHIPMENT_PICKUP_REQUIRED_BOOKING_FIELDS,
    CREATE_SHIPMENT_DROPOFF_REQUIRED_BOOKING_FIELDS
} from '@/utils/constants';
import { handleRequests, showErrorMessage } from '@/helpers';
import { GeneralMixin } from '@/mixins/GeneralMixin';

export default {
    name: 'ShipmentRequiredBookingFields',
    mixins: [GeneralMixin],
    data() {
        return {
            shipmentFields: [
                { key: 1, label: 'Service Package', checked: false, visible: true },
                { key: 2, label: 'Source Reference', checked: false, visible: true },
                { key: 4, label: 'Contact Name', checked: false, visible: true },
                { key: 8, label: 'Phone', checked: false, visible: true },
                { key: 16, label: 'Email', checked: false, visible: true },
                { key: 32, label: 'Quantity', checked: false, visible: false }, // Set to false since visibility is defined by Capacity Settings
                { key: 64, label: 'Volume', checked: false, visible: false }, // Set to false since visibility is defined by Capacity Settings
                { key: 128, label: 'Weight', checked: false, visible: false }, // Set to false since visibility is defined by Capacity Settings
                { key: 256, label: 'Notes', checked: false, visible: true },
                { key: 512, label: 'Trip Date', checked: false, visible: true }
            ],
            shipmentPickupFields: [
                { key: 1, label: 'Name', checked: false, visible: true },
                { key: 2, label: 'Phone', checked: false, visible: true },
                { key: 4, label: 'Email', checked: false, visible: true },
                { key: 8, label: 'Notes', checked: false, visible: true },
                { key: 16, label: 'Time', checked: false, visible: true }
            ],
            shipmentDropoffFields: [
                { key: 1, label: 'Name', checked: false, visible: true },
                { key: 2, label: 'Phone', checked: false, visible: true },
                { key: 4, label: 'Email', checked: false, visible: true },
                { key: 8, label: 'Notes', checked: false, visible: true },
                { key: 16, label: 'Trip Date', checked: false, visible: true },
                { key: 32, label: 'Time', checked: false, visible: true }
            ],
            shipmentFieldsBitwiseArray: [],
            shipmentPickupFieldsBitwiseArray: [],
            shipmentDropoffFieldsBitwiseArray: [],
            isDirty: false,
            requiredBookingFieldsFlags: {
                requiredStopFieldsFlags: 0,
                requiredShipmentFieldsFlags: 0,
                requiredShipmentPickupFieldsFlags: 0,
                requiredShipmentDropoffFieldsFlags: 0
            },
            vehicleCapacityUnitsConfiguration: {}
        };
    },
    computed: {
        ...mapGetters({
            user: 'user/user'
        })
    },
    async mounted() {
        await this.initializeComponent();
    },
    methods: {
        async initializeComponent() {
            await this.getTeamData();
            await this.retrieveRequiredFlags();
        },
        async retrieveRequiredFlags() {
            this.$_handleLoaderState(true);
            const api = `/api/booking-fields-flags`;
            try {
                const response = await handleRequests(api, { method: 'get' });
                this.requiredBookingFieldsFlags = response.data;
                this.shipmentFieldsBitwiseArray = ReverseBitwise(
                    this.requiredBookingFieldsFlags.requiredShipmentFieldsFlags,
                    CREATE_SHIPMENT_REQUIRED_BOOKING_FIELDS
                );
                this.shipmentPickupFieldsBitwiseArray = ReverseBitwise(
                    this.requiredBookingFieldsFlags.requiredShipmentPickupFieldsFlags,
                    CREATE_SHIPMENT_PICKUP_REQUIRED_BOOKING_FIELDS
                );
                this.shipmentDropoffFieldsBitwiseArray = ReverseBitwise(
                    this.requiredBookingFieldsFlags.requiredShipmentDropoffFieldsFlags,
                    CREATE_SHIPMENT_DROPOFF_REQUIRED_BOOKING_FIELDS
                );
                this.toggleCheckedFields(this.shipmentFields, this.shipmentFieldsBitwiseArray);
                this.toggleCheckedFields(this.shipmentPickupFields, this.shipmentPickupFieldsBitwiseArray);
                this.toggleCheckedFields(this.shipmentDropoffFields, this.shipmentDropoffFieldsBitwiseArray);
                this.$_handleLoaderState(false);
            } catch (error) {
                const message = 'Error in retrieving booking fields flags';
                showErrorMessage(this, message, error);
                this.$_handleLoaderState(false);
            }
        },
        toggleCheckedFields(fieldsArray, bitwiseArray) {
            fieldsArray.forEach((field) => {
                field.checked = bitwiseArray.includes(field.key);
            });
        },
        markDirty() {
            this.isDirty = true;
        },
        async saveChanges() {
            this.$_handleLoaderState(true);
            this.checkFieldVisibility(this.shipmentFields);
            this.checkFieldVisibility(this.shipmentPickupFields);
            this.checkFieldVisibility(this.shipmentDropoffFields);
            this.requiredBookingFieldsFlags.requiredShipmentFieldsFlags = GetBitwiseFlagsValue(this.shipmentFields);
            this.requiredBookingFieldsFlags.requiredShipmentPickupFieldsFlags = GetBitwiseFlagsValue(
                this.shipmentPickupFields
            );
            this.requiredBookingFieldsFlags.requiredShipmentDropoffFieldsFlags = GetBitwiseFlagsValue(
                this.shipmentDropoffFields
            );
            const api = `/api/booking-fields-flags/${this.requiredBookingFieldsFlags.bookingFieldsFlagId}`;
            const payload = {
                method: 'put',
                data: this.requiredBookingFieldsFlags
            };
            try {
                await handleRequests(api, payload);
                this.$notify({
                    message: 'Successfully updated',
                    type: 'success'
                });
                this.isDirty = false;
                this.$_handleLoaderState(false);
            } catch (error) {
                const message = 'Error in updating booking fields';
                showErrorMessage(this, message, error);
                this.$_handleLoaderState(false);
            }
        },
        async getTeamData() {
            const api = '/api/teams';
            const { data } = await handleRequests(api);
            this.vehicleCapacityUnitsConfiguration = data.data.vehicleCapacityUnitsConfiguration;
        },
        checkFieldVisibility(fields) {
            // Makes sure the field is not required if it is not visible
            fields.forEach((field) => {
                if (!field.visible) {
                    field.checked = false;
                }
            });
        }
    },
    watch: {
        vehicleCapacityUnitsConfiguration: {
            handler(newVal) {
                this.shipmentFields.forEach((field) => {
                    if (newVal.some((e) => e.label === field.label)) {
                        field.visible = true;
                    }
                });
            }
        }
    }
};
</script>

<style scoped>
.custom-paginated-table {
    width: 100%;
    border-collapse: collapse;
}

.md-table-head,
.md-table-cell {
    width: 50%;
    padding: 10px;
    border-top: 1px solid #ddd;
    border-bottom: 1px solid #ddd;
}

.table-gap {
    height: 10px;
}

input[type='checkbox'] {
    margin-left: 10px;
}
</style>
